import { ElementRef, Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { LocationStore } from '../stores/location.store';
import { PaymentStore } from '../stores/payment.store';
import { RateLimitStore } from '../stores/rate-limit.store';
import { AlertService } from './alert.service';
import { OrderPayStore } from '../stores/order-pay.store';
import {
  AdyenPaymentMethod,
  DigitalWalletPaymentMethods,
  StandardPaymentMethod
} from '../pages/v2-order-pay/models/payment.model';
import { IPaymentProcessor } from '../models/payment-processor/payment-processor.model';
import AdyenCheckout from '@adyen/adyen-web';
import { detect } from 'detect-browser';
import { AdyenStore } from '../stores/adyen.store';
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import { IPaymentResponse } from '../api/contracts/paymentresponse.contracts';
import { IPaymentMethodsResponse } from '../api/contracts/payment-method-response.contracts';
import { CheckoutService } from './checkout.service';
import { FullScreenLoadingService } from './full-screen-loading.service';
import { InvoicesApi } from '../api/invoices.api';
import { environment } from 'src/environments/environment';
import { RecaptchaAction, RecaptchaService } from './recaptcha.service';

@Injectable({
  providedIn: 'root'
})
export class AdyenService {
  constructor(
    private locationStore: LocationStore,
    private paymentStore: PaymentStore,
    private rateLimitStore: RateLimitStore,
    private alertService: AlertService,
    private navController: NavController,
    private orderPayStore: OrderPayStore,
    private adyenStore: AdyenStore,
    private checkoutService: CheckoutService,
    private fullscreenLoadingService: FullScreenLoadingService,
    private invoicesApi: InvoicesApi,
    private recaptchaService: RecaptchaService
  ) { }

  async goToCreditCardForm(): Promise<void> {
    if (this.rateLimitStore.exceedsPaymentLimits) {
      await this.alertService.showErrorAlert(
        this.rateLimitStore.creditCardPaymentAmountLimitMessage
      );
      return;
    }

    this.paymentStore.visitingHostedFormPage = true;

    await this.navController.navigateForward([
      '/',
      this.orderPayStore.beaconId,
      'v2',
      'adyen-card-entry'
    ]);
  }

  async initializePaymentOptions(): Promise<void> {
    // We're providing 1 for the amount as Adyen doesn't use the information, but the endpoint supports all payment providers
    const paymentMethods = await this.invoicesApi.getPaymentMethods(1);
    this.adyenStore.paymentMethods = paymentMethods;

    const mappedPaymentMethods =
      this.mapPaymentMethodsResultToAdyenMethods(paymentMethods);
    this.checkGooglePayAvailable(mappedPaymentMethods);
    this.checkApplePayAvailable(mappedPaymentMethods);
  }

  checkGooglePayAvailable(adyenPaymentMethods: AdyenPaymentMethod[]) {
    this.adyenStore.isGooglePayAvailable = adyenPaymentMethods.includes(
      AdyenPaymentMethod.GooglePay
    );
  }

  checkApplePayAvailable(adyenPaymentMethods: AdyenPaymentMethod[]) {
    const isApplePayEnabled = adyenPaymentMethods.includes(
      AdyenPaymentMethod.ApplePay
    );

    const browserInfo = detect();

    const os = browserInfo?.os ?? '';
    const isAppleOs = ['iOS', 'Mac OS'].includes(os);
    const isHttps = window.location.protocol === 'https:';

    this.adyenStore.isApplePayAvailable =
      isApplePayEnabled && isAppleOs && isHttps;
  }

  getPaymentMethodsOfType(paymentMethodType: StandardPaymentMethod) {
    return {
      paymentMethods:
        this.adyenStore.paymentMethods?.data.paymentMethods.filter(
          (pm: { name: string }) => pm.name === paymentMethodType
        )
    };
  }

  getCommonAdyenDropinConfiguration(
    paymentMethod: StandardPaymentMethod
  ): CoreOptions {
    let paymentData: any;

    return {
      paymentMethods: this.getPaymentMethodsOfType(paymentMethod),
      environment: environment.AdyenConfig.environment,
      clientKey: environment.AdyenConfig.clientKey,
      showStoredPaymentMethods: true,
      analytics: {
        enabled: true // Set to false to not send analytics data to Adyen.
      },
      onSubmit: async (state: any, dropin: any) => {
        try {
          const paymentResponse = await this.submitPayment(
            state.data,
            paymentMethod,
            true
          );
          if (!paymentResponse.success) {
            if (paymentResponse.data?.action) {
              paymentData = paymentResponse.data?.action;
              dropin.handleAction(paymentResponse.data?.action);
            } else {
              throw new Error(paymentResponse.data);
            }
          }
          if (!paymentResponse.success && paymentResponse.data?.action) {
            paymentData = paymentResponse.data?.action;
          }
        } catch (ex: any) {
          throw new Error(`Could not authorize payment: ${ex.message}`);
        }
      },
      onAdditionalDetails: async (state: any, dropin: any) => {
        try {
          const paymentResponse = await this.submitPayment(
            {
              ...state.data,
              ...paymentData
            },
            paymentMethod,
            true
          );
          if (!paymentResponse.success && paymentResponse.data?.action) {
            paymentData = paymentResponse.data?.action;
            dropin.handleAction(paymentResponse.data?.action);
          }
        } catch (ex: any) {
          throw new Error(`Could not authorize payment: ${ex.message}`);
        }
      }
    };
  }

  async initializeGooglePay(
    totalDue: number,
    googlePayRef: ElementRef,
    environment?: string
  ) {
    if (!this.adyenStore.paymentMethods) {
      throw new Error('Payment Methods not loaded');
    }
    const configuration: CoreOptions = {
      ...this.getCommonAdyenDropinConfiguration(
        StandardPaymentMethod.GooglePay
      ),

      // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
      paymentMethodsConfiguration: {
        googlepay: {
          amount: {
            value: totalDue,
            currency: this.locationStore.currentLocation?.currency ?? 'USD'
          },
          countryCode: this.locationStore.currentLocation?.countryCode,
          configuration: {
            merchantId: this.mapPaymentMethodsResultToGooglePayMerchantId(
              this.adyenStore.paymentMethods
            ),
            gatewayMerchantId:
              this.mapPaymentMethodsResultToGooglePayGatewayMerchantId(
                this.adyenStore.paymentMethods
              ),
            merchantName: this.locationStore.currentLocation?.name ?? 'Ready'
          },
          environment
        }
      }
    };

    const checkout = await AdyenCheckout(configuration);

    // Create an instance of Drop-in and mount it to the container you created.
    checkout.create('googlepay').mount(googlePayRef.nativeElement);
  }

  async initializeApplePay(totalDue: number, applePayRef: ElementRef) {
    if (!this.adyenStore.paymentMethods) {
      throw new Error('Payment Methods not loaded');
    }
    const configuration: CoreOptions = {
      ...this.getCommonAdyenDropinConfiguration(StandardPaymentMethod.ApplePay),

      // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
      paymentMethodsConfiguration: {
        applepay: {
          amount: {
            value: totalDue,
            currency: this.locationStore.currentLocation?.currency ?? 'USD'
          },
          configuration: {
            merchantId: this.mapPaymentMethodsResultToApplePayMerchantId(
              this.adyenStore.paymentMethods
            ),
            merchantName: this.locationStore.currentLocation?.name ?? 'Ready'
          },
          countryCode: this.locationStore.currentLocation?.countryCode
        }
      }
    };

    // Create an instance of AdyenCheckout using the configuration object.
    const checkout = await AdyenCheckout(configuration);

    // Create an instance of Drop-in and mount it to the container you created.
    checkout.create('applepay').mount(applePayRef.nativeElement);
  }

  private mapPaymentMethodsResultToGooglePayGatewayMerchantId(
    methods: IPaymentMethodsResponse
  ): string {
    return (
      methods.data.paymentMethods.find((pm: any) => {
        return pm.type === 'googlepay';
      })?.configuration?.gatewayMerchantId ?? ''
    );
  }

  private mapPaymentMethodsResultToGooglePayMerchantId(
    methods: IPaymentMethodsResponse
  ): string {
    return (
      methods.data.paymentMethods.find((pm: any) => {
        return pm.type === 'googlepay';
      })?.configuration?.merchantId ?? ''
    );
  }

  private mapPaymentMethodsResultToApplePayMerchantId(
    methods: IPaymentMethodsResponse
  ): string {
    return (
      methods.data.paymentMethods.find((pm: any) => {
        return pm.type === 'applepay';
      })?.configuration?.merchantId ?? ''
    );
  }

  private mapPaymentMethodsResultToAdyenMethods(
    methods: IPaymentMethodsResponse
  ): AdyenPaymentMethod[] {
    return methods.data.paymentMethods.map((pm: any) => pm.type);
  }

  private async submitPayment(
    data: { resultCode: string; sessionData: string; sessionResult: string },
    paymentMethod: StandardPaymentMethod,
    handleError: boolean = true
  ): Promise<IPaymentResponse> {
    this.fullscreenLoadingService.showFullScreenLoader();
    const paymentCard = data;

    const recaptchaToken = await this.recaptchaService.executeAction(
      RecaptchaAction.SubmitGuestPayment
    );

    return this.checkoutService.confirmPayment(
      paymentCard,
      paymentMethod,
      undefined,
      async () => { },
      undefined,
      handleError,
      true,
      recaptchaToken
    );
  }
}
